import {
  autoUpdate,
  flip,
  FloatingFocusManager,
  FloatingPortal,
  offset,
  Placement,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useId,
  useInteractions,
  useRole,
} from '@floating-ui/react'
import React, { cloneElement, useCallback, useMemo, useState } from 'react'
import { mergeRefs } from 'react-merge-refs'

import { cn } from '@sparkai/utils'

export interface AxesOffsets {
  mainAxis?: number
  crossAxis?: number
  alignmentAxis?: number | null
}

export interface PopoverProps {
  children: JSX.Element
  offsets?: AxesOffsets
  placement?: Placement
  render: (data: { close: () => void; labelId: string; descriptionId: string }) => React.ReactNode
  className?: string
}

const DEFAULT_OFFSETS = {
  mainAxis: 10,
  crossAxis: 0,
}

export const Popover = ({
  children,
  render,
  offsets = DEFAULT_OFFSETS,
  placement,
  className,
}: PopoverProps) => {
  const [open, setOpen] = useState(false)
  const closePopup = useCallback(() => setOpen(false), [])

  const { x, y, reference, floating, strategy, context } = useFloating({
    open,
    onOpenChange: setOpen,
    middleware: [offset(offsets), flip(), shift()],
    placement,
    whileElementsMounted: autoUpdate,
  })

  const id = useId()
  const labelId = `${id}-label`
  const descriptionId = `${id}-description`

  const { getReferenceProps, getFloatingProps } = useInteractions([
    useClick(context),
    useRole(context),
    useDismiss(context),
  ])

  // Preserve the consumer's ref
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const ref = useMemo(() => mergeRefs([reference, (children as any).ref]), [reference, children])

  return (
    <>
      {cloneElement(children, getReferenceProps({ ref, ...children.props }))}
      <FloatingPortal>
        {open && (
          <FloatingFocusManager
            context={context}
            modal={false}
            order={['reference', 'content']}
            returnFocus={false}
          >
            <div
              ref={floating}
              className={cn('Popover', className)}
              style={{
                position: strategy,
                top: y ?? 0,
                left: x ?? 0,
              }}
              aria-labelledby={labelId}
              aria-describedby={descriptionId}
              {...getFloatingProps()}
            >
              {render({
                labelId,
                descriptionId,
                close: closePopup,
              })}
            </div>
          </FloatingFocusManager>
        )}
      </FloatingPortal>
    </>
  )
}
